/* ─── Shared user page design system ─────────────────────────────
   Inject once via injectUserStyles() — all user pages call this.
   Tokens: #0c1118 dark, #f9ab14 amber, Bebas Neue / DM Sans / DM Mono
─────────────────────────────────────────────────────────────────── */
const SHARED_STYLE_ID = "user-pages-shared-styles-v2";

export function injectUserStyles() {
  if (document.getElementById(SHARED_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = SHARED_STYLE_ID;
  el.textContent = `
    /* Page header */
    .up-title {
      font-family:'DM Sans',sans-serif;
      font-size:24px; font-weight:700; color:#0c1118; line-height:1.2;
      text-transform:none; letter-spacing:normal;
    }
    .up-sub {
      font-family:'DM Sans',sans-serif;
      font-size:13px; color:#6b7280; margin-top:4px;
      text-transform:none; letter-spacing:normal;
    }

    /* KPI grid */
    .up-kpi-grid {
      display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:24px;
    }
    @media (max-width:1023px) { .up-kpi-grid { grid-template-columns:repeat(2,1fr); } }
    @media (max-width:479px)  { .up-kpi-grid { grid-template-columns:1fr; } }

    .up-kpi {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      padding:16px 20px; position:relative; overflow:hidden;
      transition:box-shadow 200ms;
    }
    .up-kpi:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
    .up-kpi-accent {
      position:absolute; top:0; left:0; bottom:0; width:4px;
      border-radius:10px 0 0 10px;
    }
    .up-kpi-val {
      font-family:'DM Sans',sans-serif;
      font-size:2.2rem; font-weight:700; line-height:1; color:#0c1118; letter-spacing:.02em;
    }
    .up-kpi-lbl {
      font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.06em;
      color:#9ca3af; margin-top:3px;
    }
    .up-kpi-sub { font-size:11.5px; color:#6b7280; margin-top:2px; }

    /* Panel card */
    .up-panel {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      overflow:hidden; margin-bottom:20px;
    }
    .up-panel:last-child { margin-bottom:0; }
    .up-panel-head {
      display:flex; align-items:center; justify-content:space-between;
      padding:16px 20px; border-bottom:1px solid #f3f4f6;
    }
    .up-panel-title {
      font-size:14px; font-weight:600; color:#0c1118;
      display:flex; align-items:center; gap:8px;
    }
    .up-panel-link {
      font-family:'DM Sans',sans-serif;
      font-size:11px; letter-spacing:.06em; color:#9ca3af;
      text-decoration:none; display:flex; align-items:center; gap:4px;
      transition:color 160ms;
    }
    .up-panel-link:hover { color:#000000; }

    /* List rows */
    .up-list-row {
      display:flex; align-items:center; justify-content:space-between;
      padding:14px 20px; border-bottom:1px solid #f9fafb;
      transition:background 150ms; cursor:default;
    }
    .up-list-row:last-child { border-bottom:none; }
    .up-list-row:hover { background:#fafafa; }
    .up-list-row-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .up-list-row-sub  { font-size:11.5px; color:#9ca3af; margin-top:2px; }

    /* Status badges */
    .up-badge {
      display:inline-flex; align-items:center; gap:5px;
      padding:3px 9px; border-radius:5px;
      font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.06em; font-weight:500;
    }
    .up-badge-active    { background:rgba(16,185,129,.1);  color:#065f46; }
    .up-badge-draft     { background:#f3f4f6;               color:#6b7280; }
    .up-badge-archived  { background:rgba(239,68,68,.08);   color:#991b1b; }
    .up-badge-pending   { background:rgba(249,171,20,.12);  color:#92400e; }
    .up-badge-approved  { background:rgba(16,185,129,.1);   color:#065f46; }
    .up-badge-rejected  { background:rgba(239,68,68,.1);    color:#991b1b; }
    .up-badge-sent      { background:rgba(99,102,241,.1);   color:#3730a3; }
    .up-badge-accepted  { background:rgba(5,150,105,.1);    color:#064e3b; }
    .up-badge-default   { background:#f3f4f6;               color:#6b7280; }

    /* Quick action links */
    .up-qa-link {
      display:flex; align-items:center; gap:12px;
      padding:13px 20px; border-bottom:1px solid #f3f4f6;
      text-decoration:none; transition:background 150ms;
    }
    .up-qa-link:last-child { border-bottom:none; }
    .up-qa-link:hover { background:#fafafa; }
    .up-qa-icon {
      width:34px; height:34px; border-radius:8px; background:#f4f5f7;
      display:flex; align-items:center; justify-content:center;
      flex-shrink:0; color:#0c1118; transition:background 150ms;
    }
    .up-qa-link:hover .up-qa-icon { background:rgba(249,171,20,.15); color:#b45309; }
    .up-qa-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .up-qa-desc { font-size:11.5px; color:#9ca3af; margin-top:1px; }
    .up-qa-arrow { color:#d1d5db; flex-shrink:0; margin-left:auto; transition:transform 150ms, color 150ms; }
    .up-qa-link:hover .up-qa-arrow { transform:translateX(3px); color:#f9ab14; }

    /* Buttons */
    .up-btn {
      display:inline-flex; align-items:center; gap:7px;
      padding:8px 16px; border-radius:7px; font-size:13px; font-weight:500;
      cursor:pointer; border:none; transition:background 150ms;
      font-family:'DM Sans',sans-serif;
    }
    .up-btn-dark    { background:#0c1118; color:#fff; }
    .up-btn-dark:hover { background:#1e2832; }
    .up-btn-dark:disabled { opacity:.5; cursor:default; }
    .up-btn-amber   { background:#f9ab14; color:#0c1118; }
    .up-btn-amber:hover { background:#d97706; }
    .up-btn-white   { background:#ffffff; color:#111111; }
    .up-btn-white:hover { background:#f0f0f0; }
    .up-btn-outline { background:#fff; color:#374151; border:1px solid #e2e8f0; }
    .up-btn-outline:hover { background:#f9fafb; }

    /* Spinner */
    .up-spinner {
      width:18px; height:18px; border:2px solid #e8ecf0;
      border-top-color:#0c1118; border-radius:50%;
      animation:upSpin .7s linear infinite; flex-shrink:0;
    }
    @keyframes upSpin { to { transform:rotate(360deg); } }

    /* Empty / loading */
    .up-empty { padding:48px 20px; text-align:center; }
    .up-empty-title { font-size:14px; font-weight:600; color:#374151; margin-top:12px; }
    .up-empty-sub   { font-size:12px; color:#9ca3af; margin-top:4px; }
    .up-loading {
      display:flex; align-items:center; justify-content:center; gap:10px;
      padding:48px 20px; font-size:13px; color:#9ca3af;
    }

    /* Two-col layout */
    .up-two-col { display:grid; grid-template-columns:1fr 300px; gap:20px; }
    @media (max-width:1023px) { .up-two-col { grid-template-columns:1fr; } }

    /* Three-col layout */
    .up-three-col { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
    @media (max-width:1023px) { .up-three-col { grid-template-columns:1fr; } }

    /* Field label */
    .up-field-label {
      font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.04em;
      color:#6b7280; display:block; margin-bottom:6px;
    }

    /* CTA banner */
    .up-cta-banner {
      background:#0c1118; border-radius:10px;
      padding:20px 24px; display:flex; align-items:center; justify-content:space-between;
      gap:16px; flex-wrap:wrap;
    }
    .up-cta-title { font-family:'DM Sans',sans-serif; font-size:1rem; font-weight:700; color:#fff; }
    .up-cta-sub   { font-size:12px; color:rgba(255,255,255,.5); margin-top:2px; }
  `;
  document.head.appendChild(el);
}

export function statusBadgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "active")   return "up-badge up-badge-active";
  if (s === "draft")    return "up-badge up-badge-draft";
  if (s === "archived") return "up-badge up-badge-archived";
  if (s.includes("pending")) return "up-badge up-badge-pending";
  if (s === "approved" || s === "completed") return "up-badge up-badge-approved";
  if (s === "rejected" || s === "declined")  return "up-badge up-badge-rejected";
  if (s === "sent_to_client") return "up-badge up-badge-sent";
  if (s === "accepted") return "up-badge up-badge-accepted";
  return "up-badge up-badge-default";
}

export function fmtStatus(s) {
  return (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
